import { pdf } from "@react-pdf/renderer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Department } from "../../../contexts/AuthProvider";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import type { NurseInShift, ScheduleData } from "../../../utils/models";
import { StaffingPDF } from "../../StaffingPDF";
import { PREVIEW_DEBOUNCE_MS, PREVIEW_SETTLE_MS } from "./consts";

/** Everything `StaffingPDF` reads. Compared by identity to decide whether built bytes are still current. */
type BuildInputs = {
	weekIndices: number[];
	schedule: ScheduleData;
	chosenDepartment: Department;
	isSnf: boolean;
	ptoShiftsByDate: Record<string, NurseInShift[]>;
	/** The department's name-order flag (`useLastNameFirst()`). */
	lastNameFirst: boolean;
};

type BuiltDocument = BuildInputs & {
	blob: Blob;
	/** Object URL for the viewer. Created here, so revoked here. */
	url: string;
	/**
	 * The debounced key this document was built for.
	 *
	 * Separate from the inputs above, and load-bearing: `schedule` gets a new identity on any of the
	 * two dozen staffing updates the provider makes, so judging what is ON SCREEN by input identity left
	 * a finished preview permanently "stale" — a spinner with nothing able to clear it, because the key
	 * had not changed and the build effect never refired. The key answers "is the preview current"; the
	 * inputs answer the stricter "are these bytes safe to hand over as the file".
	 */
	key: string;
};

/**
 * Whether built bytes still describe the current inputs.
 *
 * Identity comparison is enough because every one of these is either a primitive, a context value, or a
 * react-query cached value — none is rebuilt per render. `weekIndices` is compared element-wise since
 * it is derived, and a fresh array holding the same weeks must not count as a change.
 */
const sameInputs = (a: BuildInputs, b: BuildInputs): boolean =>
	a.schedule === b.schedule &&
	a.chosenDepartment === b.chosenDepartment &&
	a.isSnf === b.isSnf &&
	a.ptoShiftsByDate === b.ptoShiftsByDate &&
	a.lastNameFirst === b.lastNameFirst &&
	a.weekIndices.length === b.weekIndices.length &&
	a.weekIndices.every((weekIndex, position) => weekIndex === b.weekIndices[position]);

/**
 * True once the preview has been continuously visible for `delayMs`.
 *
 * Deliberately not `useDebouncedValue`: that hook seeds its state with the incoming value, so on mount
 * it reports "settled" on the first render — and opening the popup animates exactly like maximizing
 * it. Going back to false the moment visibility drops means minimizing cancels a build that has not
 * started yet.
 */
const useSettledVisibility = (visible: boolean, delayMs: number): boolean => {
	const [settled, setSettled] = useState(false);

	useEffect(() => {
		if (!visible) {
			setSettled(false);
			return;
		}
		const timer = setTimeout(() => setSettled(true), delayMs);
		return () => clearTimeout(timer);
	}, [visible, delayMs]);

	return settled;
};

type Params = BuildInputs & {
	/** The exported dates: with the cache on, positions start at 0 for any range, so they alone don't name it. */
	rangeKey: string;
	/** False while the preview can't be seen, so a multi-page document is never rendered unseen. */
	enabled: boolean;
	onError: (error: unknown) => void;
};

/**
 * Render the weekly staffing PDF, and hand back the same bytes for the viewer and the download.
 *
 * Built with the bare `pdf()` renderer rather than `usePDF`, for two reasons that both come down to
 * knowing WHICH inputs the bytes on hand describe:
 *
 * 1. **Provenance.** `usePDF` reports `loading` off a `change` event, not off completion: it queues at
 *    concurrency 1 and a job that starts while another is running finishes with `loading` already back
 *    to false. The download would then hand over the previous week's document under the new week's
 *    filename. Here every build is tagged with the inputs that produced it, so "is this stale" is an
 *    identity comparison instead of a guess.
 * 2. **A mounted `usePDF` is not inert.** react-pdf keeps its change listeners in a module-global list
 *    and shares one reconciler, so ANY other `pdf()` call in the app commits into it and notifies the
 *    mounted instance — which then calls `toBlob()` on a container whose document may be null and
 *    reports a `TypeError` that has nothing to do with this preview.
 *
 * `pdf()` used without `.on("change")` registers no global listener, so builds here are isolated and
 * safe to overlap.
 */
export const useWeeklyPdfPreview = ({ weekIndices, rangeKey, schedule, chosenDepartment, isSnf, ptoShiftsByDate, lastNameFirst, enabled, onError }: Params) => {
	const [built, setBuilt] = useState<BuiltDocument | null>(null);
	const [isBuilding, setIsBuilding] = useState(false);
	const [error, setError] = useState<unknown>(null);
	const [retryNonce, setRetryNonce] = useState(0);

	// A cheap serializable summary of the inputs worth COALESCING on. Debouncing this rather than the
	// document itself means dragging a date picker produces one build instead of one per tick.
	// Deliberately excludes `enabled`: visibility is not a property of the document, and including it
	// meant every minimize -> maximize round trip changed the key and re-rendered an identical PDF.
	const documentKey = useMemo(
		() =>
			[
				chosenDepartment.department_id,
				isSnf ? "snf" : "hospital",
				weekIndices.join(","),
				rangeKey,
				Object.keys(ptoShiftsByDate).sort().join(","),
				lastNameFirst ? "last-first" : "first-last",
			].join("|"),
		[chosenDepartment.department_id, isSnf, weekIndices, rangeKey, ptoShiftsByDate, lastNameFirst],
	);
	const debouncedKey = useDebouncedValue(documentKey, PREVIEW_DEBOUNCE_MS);
	const settled = useSettledVisibility(enabled, PREVIEW_SETTLE_MS);

	// The live inputs are read through a ref so the build effect depends only on its triggers, not on
	// `schedule`'s identity changing on every parent render.
	const inputs: BuildInputs = { weekIndices, schedule, chosenDepartment, isSnf, ptoShiftsByDate, lastNameFirst };
	const inputsRef = useRef(inputs);
	inputsRef.current = inputs;

	const builtRef = useRef<BuiltDocument | null>(built);
	builtRef.current = built;

	/** Rising counter so a superseded build cannot publish its bytes over a newer one. */
	const generationRef = useRef(0);
	/**
	 * The (key, retry) pair a build was last kicked off for, making the effect idempotent.
	 *
	 * StrictMode double-invokes the mount effect, which otherwise starts two full renders of the same
	 * multi-page document that then race each other for the main thread — a stall in dev that looks
	 * exactly like the bug this hook already had.
	 */
	const startedTokenRef = useRef<string | null>(null);
	// Set on the way IN as well as cleared on the way out: StrictMode runs an effect, tears it down and
	// runs it again on mount, so a flag only cleared in the cleanup stays false for the component's whole
	// life — and every finished build then gets discarded as "unmounted", leaving the spinner forever.
	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const render = useCallback(
		({
			weekIndices: indices,
			schedule: data,
			chosenDepartment: department,
			isSnf: snf,
			ptoShiftsByDate: pto,
			lastNameFirst: reversed,
		}: BuildInputs): Promise<Blob> =>
			pdf(
				<StaffingPDF weekIndices={indices} schedule={data} chosenDepartment={department} isSnf={snf} ptoShiftsByDate={pto} lastNameFirst={reversed} />,
			).toBlob(),
		[],
	);

	// `debouncedKey`, `settled` and `retryNonce` are the triggers; the inputs themselves are read from a
	// ref and compared against what was last built, which is what decides whether a build is needed at
	// all. Depending on the inputs directly would both bypass the debounce and rebuild an identical
	// document every time the popup is maximized.
	useEffect(() => {
		if (!settled) return;
		const current = inputsRef.current;
		if (current.weekIndices.length === 0) return;
		// Already built for exactly this key — reuse it rather than re-rendering the same document.
		if (builtRef.current?.key === debouncedKey) return;
		const token = `${debouncedKey}|${retryNonce}`;
		if (startedTokenRef.current === token) return;
		startedTokenRef.current = token;

		const generation = generationRef.current + 1;
		generationRef.current = generation;
		setIsBuilding(true);
		setError(null);

		render(current)
			.then((blob) => {
				// A newer build already started, or the popup closed: publishing now would either overwrite
				// fresher bytes or leak an object URL nobody can revoke.
				if (generation !== generationRef.current || !isMountedRef.current) return;
				setBuilt({ ...current, blob, url: URL.createObjectURL(blob), key: debouncedKey });
				setIsBuilding(false);
			})
			.catch((buildError: unknown) => {
				if (generation !== generationRef.current || !isMountedRef.current) return;
				setError(buildError);
				setIsBuilding(false);
			});
	}, [debouncedKey, settled, retryNonce, render]);

	// Revokes the PREVIOUS url when `built` is replaced, and the last one on unmount. Safe against the
	// viewer: React commits the new `src` before running this cleanup. The download holds the blob, not
	// the url, so a revoked url never costs us a file.
	useEffect(() => {
		if (!built) return;
		return () => URL.revokeObjectURL(built.url);
	}, [built]);

	// `error` is state, so reporting it from render would re-toast on every one. One report per failure.
	const reportedError = useRef<unknown>(null);
	useEffect(() => {
		if (!error) {
			reportedError.current = null;
			return;
		}
		if (reportedError.current === error) return;
		reportedError.current = error;
		onError(error);
	}, [error, onError]);

	/**
	 * The bytes to download: the preview's own whenever they describe the current inputs, freshly built
	 * when they do not.
	 *
	 * The stale case is reachable and not exotic — the minimized card never builds a preview, a viewer
	 * with no inline PDF renderer never builds one, and editing the schedule behind this non-modal popup
	 * changes `schedule` without changing the debounced key. The file must be right in all of them.
	 */
	const getDownloadBlob = useCallback(async (): Promise<Blob> => {
		const current = inputsRef.current;
		if (builtRef.current && sameInputs(builtRef.current, current)) return builtRef.current.blob;
		return render(current);
	}, [render]);

	const retry = useCallback(() => setRetryNonce((nonce) => nonce + 1), []);

	// Keyed, not identity-based: see the note on `BuiltDocument.key`.
	const isShowingCurrentDocument = built?.key === documentKey;

	return {
		url: built?.url ?? null,
		getDownloadBlob,
		retry,
		/**
		 * Exact rather than inferred: anything other than "bytes on hand that match the current inputs"
		 * is loading, which covers the debounce window, the settle window and an in-flight build alike.
		 */
		isLoading: enabled && weekIndices.length > 0 && !error && (isBuilding || !isShowingCurrentDocument),
		hasError: Boolean(error),
	};
};
